/**
 * Copy trading, as a page of its own: who you copy, who may copy you, the invitation that lets a
 * person in, and — for whoever runs the server — whether copied orders are really sent. The same
 * body is the "Copy trading" section of Settings.
 */
import { useEffect, useState } from 'react'
import { copyApi, invitesApi, type Invite } from './api'
import { Person, StartCopy, useCopies } from './Copy'
import { Status, Switch, askSure, usd } from './kit'
import { Bar, Page } from './Page'
import { Group, Row, Rows, note, quiet } from './rows'
import { useDemo } from './store'

const day = (ms: number) => new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })

/** Invitations: one link lets one person in. They get a Liberabank of their own. */
function Invitations() {
  const say = useDemo((s) => s.say)
  const [data, setData] = useState<{ invites: Invite[]; people: { id: string; name: string; joinedAt: number }[] } | null>(null)
  const load = () => void invitesApi.list().then(setData, () => setData({ invites: [], people: [] }))
  useEffect(load, [])
  const open = (data?.invites ?? []).filter((i) => !i.usedBy && i.expiresAt > Date.now())
  const copy = (url: string) => void navigator.clipboard.writeText(url).then(() => say('Link copied — send it to them'), () => say(url))
  return (
    <Group title="Invite someone" note="One link, one person">
      <Rows>
        {open.map((i) => (
          <Row
            key={i.token}
            title={i.url.replace(/^https?:\/\//, '')}
            sub={`Not used yet · good until ${day(i.expiresAt)}`}
            right={
              <span className="flex shrink-0 items-center">
                <button type="button" className={quiet} onClick={() => copy(i.url)}>
                  Copy link
                </button>
                <button type="button" className={quiet} onClick={() => void invitesApi.revoke(i.token).then(load)}>
                  Take back
                </button>
              </span>
            }
          />
        ))}
        {(data?.people ?? []).map((p) => (
          <Row key={p.id} left={<Person name={p.name} />} title={p.name} sub={`Joined ${day(p.joinedAt)} with your invitation`} right={<Status tone="free">Has an account</Status>} />
        ))}
        <Row
          title="A new invitation"
          sub="They get their own Liberabank, accept the terms themselves, and connect their own accounts"
          right={
            <button type="button" className={quiet} onClick={() => void invitesApi.create().then((i) => (load(), copy(i.url)), (e) => say(e instanceof Error ? e.message : 'That did not work'))}>
              Make a link
            </button>
          }
        />
      </Rows>
    </Group>
  )
}

/** The server's own switch, for whoever runs it: rehearsal (nothing is sent) or live. */
function CopyMode({ live, onChanged }: { live: boolean; onChanged: () => void }) {
  const say = useDemo((s) => s.say)
  const set = (on: boolean) => void copyApi.setMode(on).then(() => (say(on ? 'Copying is live: follower orders are sent' : 'Copying is in rehearsal: nothing is sent'), onChanged()), (e) => say(e instanceof Error ? e.message : 'That did not work'))
  return (
    <Group title="This server" note="Only you see this">
      <Rows>
        <Row
          title="Send copied orders for real"
          sub={live ? 'Live: a follower’s copy sends real orders to their own broker' : 'Rehearsal: every copied trade is worked out and written down, none is sent'}
          right={
            <Switch
              on={live}
              onChange={(v) => (v ? askSure({ title: 'Go live with copy trading?', body: 'From now on, when someone copies a trade, a real order goes to their own brokerage account. Watch a rehearsal first: a copy’s page shows what it would have sent.', cta: 'Go live', done: () => set(true) }) : set(false))}
              label="Send copied orders for real"
            />
          }
        />
      </Rows>
    </Group>
  )
}

/** Who you copy, who may copy you, how a person gets in. */
export function CopyHub() {
  const { data, load } = useCopies()
  const admin = useDemo((s) => s.user?.role === 'admin')
  const settings = useDemo((s) => s.settings)
  const save = useDemo((s) => s.saveSettings)
  const openCopy = useDemo((s) => s.openCopy)
  const [leader, setLeader] = useState<{ id: string; name: string } | null>(null)
  if (!data) return <div className="h-24 animate-ws-pulse rounded-[8px] border border-ws-line" />
  const allow = settings?.copy?.allow === true
  const mine = data.copies.filter((c) => c.status !== 'stopped')
  const free = data.leaders.filter((l) => !l.copying)
  return (
    <>
      {!data.live && (
        <div className="rounded-[8px] border border-ws-line px-3 py-[10px] text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
          <span className="text-ws-warn">Rehearsal.</span> Copying is being tried out on this server: every copied trade is worked out and written down, and nothing is sent to a broker.
        </div>
      )}
      <Group title="People you copy" note="Each is its own sub-account in the portfolio">
        <Rows>
          {mine.map((c) => (
            <Row
              key={c.id}
              left={<Person name={c.leader.name} />}
              title={c.leader.name}
              sub={`Since ${day(c.since)} · ${usd(c.putIn)} put in · worth ${usd(c.value)}`}
              right={
                <button type="button" className={quiet} onClick={() => openCopy(c.id)}>
                  Open
                </button>
              }
            />
          ))}
          {free.map((l) => (
            <Row
              key={l.id}
              left={<Person name={l.name} />}
              title={l.name}
              sub="Lets you copy their trades"
              right={
                <button type="button" className={quiet} onClick={() => setLeader(l)}>
                  Start copying
                </button>
              }
            />
          ))}
          {!mine.length && !free.length && <Row title="Nobody yet" sub={admin ? 'Someone with an account here shows up once they switch on “may copy my trades”' : 'The person who invited you shows up here once they let their trades be copied'} />}
        </Rows>
        <p className={note}>You choose how much money follows a person and your own limits. That money is kept apart from the rest of your account, and you can stop at any time.</p>
      </Group>
      <Group title="People who copy you">
        <Rows>
          <Row title="People I invited may copy my trades" sub="They switch it on themselves, with their own money and their own limits" right={<Switch on={allow} onChange={(v) => void save({ copy: { allow: v } })} label="People I invited may copy my trades" />} />
          {data.followers.map((f, i) => (
            <Row key={i} left={<Person name={f.name} />} title={f.name} sub={`Since ${day(f.since)}`} right={<Status tone={f.status === 'active' ? 'free' : 'off'}>{f.status === 'active' ? 'Copying' : f.status === 'paused' ? 'Paused' : 'Stopped'}</Status>} />
          ))}
        </Rows>
        <p className={note}>You never see what they hold or how much they put in, and you place no order for them: their own account copies yours, by rules they set and can stop at any time.</p>
      </Group>
      {admin ? (
        <Invitations />
      ) : (
        <Group title="Invite someone">
          <Rows>
            <Row title="Invitations are made by whoever runs this server" sub="Ask them for a link for the person you want to bring in" />
          </Rows>
        </Group>
      )}
      <Group title="How it works">
        <Rows>
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">1</span>} title="Send an invitation" sub="The person signs up with the link, accepts the terms and connects their own broker" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">2</span>} title="The one to be copied allows it" sub="On their own account: “People I invited may copy my trades”" />
          <Row left={<span className="w-4 shrink-0 text-center text-[12px] tabular-nums text-ws-fg-4">3</span>} title="The one who copies starts it" sub="Under People you copy: Start copying, with an amount of money and limits of their own" />
        </Rows>
      </Group>
      {admin && <CopyMode live={data.live} onChanged={load} />}
      <StartCopy leader={leader} onClose={() => setLeader(null)} onStarted={load} />
    </>
  )
}

export const CopyBar = () => <Bar>{<span className="px-2 text-[13px] tracking-normal text-ws-fg-3">Follow someone’s trades with your own money and your own limits</span>}</Bar>
export function CopyView() {
  return (
    <Page className="mx-auto max-w-[720px]">
      <div className="space-y-6">
        <CopyHub />
      </div>
    </Page>
  )
}
